import React, { useEffect, useRef } from 'react';
import * as THREE from "three";
import { Category as WebCategory } from '../../../../types/models/Category';
import { getImageURLCategoryListIcon, getImageURLOnServerBeaconFace, getModelURLOnServer } from '../../../../utils/utils';
import { Category } from '../../../three/category';
import { Beacon } from '../../../three/beacon';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { loadBeaconModel, updateBeaconModelName } from "../../../three/beacon-loader";
import { generateUUID } from 'three/src/math/MathUtils';

interface BeaconModelPreviewProps {
    category: WebCategory | null,
    beaconImageId: string | undefined,
    beaconImage: File | undefined,
    beaconName: string
}

// Need a single constant renderer
const renderer = new THREE.WebGLRenderer({ antialias: true });

// The last ID generated for rendering
let lastRendererID = generateUUID();
let lastModel: THREE.Object3D | null = null;

const BeaconModelPreview: React.FC<BeaconModelPreviewProps> = (props: BeaconModelPreviewProps) => {
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (containerRef.current) {
            let container = containerRef.current;

            let rendererID = generateUUID();
            lastRendererID = rendererID;

            let name = props.beaconName == "" ? "Beacon Name" : props.beaconName;
            let color = props.category?.Color ?? "C0C0C0";
            let showDistance = props.category?.ShowDistance ?? true;

            let asset: string;
            if (props.category?.Theme == "planet") {
                asset = getModelURLOnServer("planet3.glb");
            } else if (props.category?.Theme == "trisign") {
                asset = getModelURLOnServer("trisign3.glb");
            } else if (props.category?.Theme == "ballnoball") {
                asset = getModelURLOnServer("ballnoball3.glb");
            } else if (props.category?.Theme == "paw") {
                asset = getModelURLOnServer("paw.glb");
            } else if (props.category?.Theme == "billboardwide") {
                asset = getModelURLOnServer("billboardwide.glb");
            } else if (props.category?.Theme == "billboardtall") {
                asset = getModelURLOnServer("billboardtall.glb");
            } else {
                asset = getModelURLOnServer("ball3.glb");
            }

            let imageURL: string;
            if (props.beaconImage) {
                imageURL = URL.createObjectURL(props.beaconImage);
            } else if (props.beaconImageId) {
                imageURL = getImageURLOnServerBeaconFace(props.beaconImageId, "");
            } else if (props.category?.Id) {
                imageURL = getImageURLCategoryListIcon(props.category?.Id, "");
            } else {
                imageURL = "/images/beacon-face-fallback.jpg";
            }

            let badgeURL: string;
            if (props.category?.Id) {
                badgeURL = getImageURLCategoryListIcon(props.category.Id, "");
            } else {
                badgeURL = "/images/beacon-face-fallback.jpg";
            }

            const containerWidth = container.clientWidth;
            const containerHeight = container.clientHeight;
            renderer.setSize(containerWidth, containerHeight);
            renderer.setPixelRatio(2);
            container.appendChild(renderer.domElement);
            console.log("Added");

            const scene = new THREE.Scene();
            scene.background = new THREE.Color(1, 1, 1);
            const camera = new THREE.PerspectiveCamera(25, containerWidth / containerHeight, 0.1, 1000);
            camera.position.z = 2.8;

            const ambientLight = new THREE.AmbientLight(0xffffff, 0.5);
            scene.add(ambientLight);
            const directionalLight = new THREE.DirectionalLight(0xffffff, 0.8);
            directionalLight.position.set(1, 2, 3).normalize();
            scene.add(directionalLight);

            const category: Category = {
                name: "",
                description: "",
                color: color,
                beacon_height: "",
                icon: badgeURL,
                beacon_image_a: imageURL,
                beacon_image_b: imageURL,
                beacon_image_c: imageURL,
                glb_model3: asset,
                // glb_model3: "/3d-assets/ball3.glb",
                // glb_model3: "/3d-assets/planet3.glb",
                // glb_model3: "/3d-assets/trisign3.glb",
                // glb_model3: "/3d-assets/ballnoball3.glb",
                // glb_model3: "/3d-assets/billboardwide.glb",
                // glb_model3: "/3d-assets/billboardtall.glb",
                // glb_model3: "/3d-assets/paw.glb",
                show_distance: showDistance,
                mindistancevisible: null,
                maxdistancevisible: null,
                use_yards: false
            };

            const beacon: Beacon = {
                latitude: 0,
                longitude: 0,
                id: "",
                category: "",
                display_name: name,
                display_description: "",
                link: null,
                beacon_image_a: null,
                beacon_image_b: null,
                beacon_image_c: null,
                // beacon_image_a: "/images/temp/tall-dune.jpg",
                // beacon_image_b: "/images/temp/tall-furiosa.jpg",
                // beacon_image_c: "/images/temp/tall-challengers.jpg",
                badge_image: null,
                // badge_image: "/images/temp/category-brave.jpg",
                show_distance: null,
                glb_model3: null,
                start_time: null,
                end_time: null,
                like_count: 0,
                image_url: null,
                image_width: null,
                image_height: null
            };

            const loader = new GLTFLoader();
            loader.load(
                category.glb_model3,
                (gltf) => {
                    if (lastRendererID != rendererID) { return; }

                    const animations = gltf.animations;

                    loadBeaconModel({
                        model: gltf.scene,
                        beacon: beacon,
                        category: category,
                        initialLocation: { latitude: beacon.latitude + 0.0002, longitude: beacon.longitude },
                        defaultImageURL: "/images/beacon-face-fallback.jpg",
                        modelFailed: () => {
                            // Error here
                            console.log("Model failed!");
                        },
                        modelLoaded: (model) => {
                            if (lastRendererID != rendererID) { return; }

                            // Extra model loading things here
                            scene.add(model);

                            const modelBoundingBox = new THREE.Box3().setFromObject(model);
                            const maxScale = Math.max(
                                modelBoundingBox.max.x - modelBoundingBox.min.x,
                                modelBoundingBox.max.y - modelBoundingBox.min.y,
                                modelBoundingBox.max.z - modelBoundingBox.min.z
                            );

                            model.position.set(0, -(modelBoundingBox.max.y - modelBoundingBox.min.y) / 2 / maxScale, 0);

                            const scale = 1 / maxScale;
                            model.scale.set(scale, scale, scale);

                            const mixer = new THREE.AnimationMixer(model);

                            let targetRotation = 0;
                            let currentRotation = Math.sin(Date.now() * 0.001 * 0.5) * Math.PI / 12;
                            if (animations.length == 0) {
                                model.rotation.y = targetRotation;
                            }

                            const clock = new THREE.Clock();
                            function animate() {
                                if (lastRendererID != rendererID) { return; }

                                requestAnimationFrame(animate);

                                if (animations.length == 0) {
                                    targetRotation = Math.sin(Date.now() * 0.001 * 0.5) * Math.PI / 12;
                                    currentRotation = THREE.MathUtils.lerp(currentRotation, targetRotation, 0.1);
                                    model.rotation.y = currentRotation;
                                }

                                mixer.update(clock.getDelta());
                                renderer.render(scene, camera);
                            }

                            animations.forEach(function (clip) {
                                mixer.clipAction(clip).play();
                            });

                            animate();

                            lastModel = model;
                        }
                    });
                },
                undefined,
                (error) => {
                    console.error('An error occurred while loading the model:', error);
                }
            );

            return () => {
                lastRendererID = generateUUID();
                lastModel = null;

                if (scene) {
                    scene.traverse((object) => {
                        if (object instanceof THREE.Mesh) {
                            object.geometry.dispose();

                            if (Array.isArray(object.material)) {
                                object.material.forEach(material => {
                                    material.dispose();
                                    if (material.map) material.map.dispose();
                                });
                            } else {
                                object.material.dispose();
                                if (object.material.map) object.material.map.dispose();
                            }
                        }
                    });

                    scene.removeFromParent();
                    renderer.domElement.remove();

                    console.log("Removed");
                }
            };
        }
    }, [props.category, props.beaconImageId, props.beaconImage]);

    useEffect(() => {
        if (containerRef.current && lastModel) {
            let showDistance = props.category?.ShowDistance ?? true;

            updateBeaconModelName({
                model: lastModel,
                beaconName: props.beaconName,
                showDistance: showDistance
            });
        }
    }, [props.beaconName]);

    return (
        <div
            id='beacon-canvas'
            ref={containerRef}
            style={{
                width: '400px',
                height: '400px',
                backgroundColor: 'white',
            }}
        >
        </div>
    );
};

export default BeaconModelPreview;
